import { ClaimSessionEndedLogout, GetLanguage, GetRefreshToken, IsSessionEnded } from '@/utils-ts'
import { TokenManager } from '@/utils-ts/TokenManager'
import axios, { type AxiosInstance } from 'axios'

const redirectToLogin = () => {
  if (IsSessionEnded() && !ClaimSessionEndedLogout()) return
  if (window.location.pathname !== '/login') window.location.href = '/login'
}
export class AxiosManager {
  private static axiosInstance: AxiosInstance | null = null
  private static isRefreshing = false
  private static refreshSubscribers: {
    resolve: (token: string) => void
    reject: (error: any) => void
  }[] = []

  public static GetAxiosInstance(): AxiosInstance {
    if (!AxiosManager.axiosInstance) {
      AxiosManager.axiosInstance = axios.create({ withCredentials: true })
      this.SetupInterceptors(AxiosManager.axiosInstance)
    }
    return AxiosManager.axiosInstance
  }

  private static SetupInterceptors(instance: AxiosInstance) {
    instance.interceptors.request.use(
      async config => {
        config.headers.set('X-Platform', 'Admin')

        const tokenManager = TokenManager.GetInstance()

        // PROAKTİF KONTROL
        const isRefreshRequest = config.url?.includes('Login/RefreshToken')
        if (IsSessionEnded() && !isRefreshRequest) {
          const error = new Error('Session expired')
          redirectToLogin()
          return Promise.reject(error)
        }
        if (tokenManager.IsTokenExpired() && !isRefreshRequest) {
          if (AxiosManager.isRefreshing) {
            console.log('--- TOKEN EXPIRED (PROACTIVE), QUEUEING REQUEST (ADMIN) ---', config.url)
            return new Promise((resolve, reject) => {
              AxiosManager.refreshSubscribers.push({
                resolve: (token: string) => {
                  config.headers.set('Authorization', `Bearer ${token}`)
                  resolve(config)
                },
                reject,
              })
            })
          }

          AxiosManager.isRefreshing = true
          try {
            console.log('--- TOKEN EXPIRED (PROACTIVE), REFRESHING START (ADMIN) ---')
            const outcome = await tokenManager.RefreshAccessToken()
            if (outcome.status === 'ok') {
              AxiosManager.isRefreshing = false
              AxiosManager.onTokenRefreshed(outcome.token)
              config.headers.set('Authorization', `Bearer ${outcome.token}`)
            } else if (outcome.status === 'transient') {
              const existing = tokenManager.GetToken()
              if (existing) {
                AxiosManager.onTokenRefreshed(existing)
                config.headers.set('Authorization', `Bearer ${existing}`)
              } else {
                const err = new Error('Refresh temporarily unavailable')
                AxiosManager.onTokenRefreshFailed(err)
                return Promise.reject(err)
              }
            } else {
              const err = new Error('Session expired')
              AxiosManager.onTokenRefreshFailed(err)
              redirectToLogin()
              return Promise.reject(err)
            }
          } catch (e) {
            AxiosManager.isRefreshing = false
            AxiosManager.onTokenRefreshFailed(e)
            throw e
          } finally {
            AxiosManager.isRefreshing = false
          }
        }

        const token = tokenManager.GetToken()
        if (token) {
          config.headers.set('Authorization', `Bearer ${token}`)
        }

        const refreshToken = GetRefreshToken()
        if (refreshToken) {
          config.headers.set('X-Refresh-Token', refreshToken)
        }

        const language = GetLanguage()

        if (language?.id) {
          config.headers.set('LanguageId', language.id)
        }

        config.headers.set('X-Timezone-Id', Intl.DateTimeFormat().resolvedOptions().timeZone)

        const offsetHours = -new Date().getTimezoneOffset() / 60
        config.headers.set('X-Timezone-Offset', offsetHours.toString())

        config.params = {
          ...config.params,
          locale: language.code,
        }

        // DEV guard: giden veride offset'li tarih / Date nesnesi var mı kontrol et
        if (import.meta.env.DEV) {
          this.detectTimezone(config.data, 'REQUEST', config.url)
        }

        return config
      },
      error => Promise.reject(error),
    )

    instance.interceptors.response.use(
      async response => {
        // --- SILENT MIGRATION HANDLER ---
        // If the server signals that the session was upgraded (migration),
        // we trigger a background token refresh to sync our local state (cookies/storage).
        const migrationToken = response.headers['x-new-refresh-token']
        if (migrationToken) {
          const { TokenManager } = await import('@/utils-ts/TokenManager')
          const { SetRefreshToken } = await import('@/utils-ts/Cookie')
          SetRefreshToken(migrationToken)
          TokenManager.GetInstance().Initialize() // Refresh internal state
        }

        if (response.headers['x-session-upgraded'] === 'true') {
          // Sinyali aldık, token zaten yukarıda setRefreshToken ile kaydedildi.
          // Hemen yenileme yapmamıza gerek yok, Access Token bitince doğal akışında yenilenecek.
          console.log('--- SESSION SILENTLY MIGRATED (ADMIN) ---')
        }
        return response
      },
      async error => {
        const originalRequest = error.config

        const status = error.response?.status
        if (status === 401 && IsSessionEnded()) {
          redirectToLogin()
          throw error
        }
        if (status !== 401 || !originalRequest || originalRequest._retry) {
          throw error
        }

        const tokenManager = TokenManager.GetInstance()
        const currentToken = tokenManager.GetToken()
        const sentAuthorization =
          originalRequest.headers?.get?.('Authorization') ??
          originalRequest.headers?.['Authorization'] ??
          originalRequest.headers?.['authorization']
        if (
          currentToken &&
          !tokenManager.IsTokenExpired() &&
          sentAuthorization !== `Bearer ${currentToken}`
        ) {
          originalRequest._retry = true
          originalRequest.headers.set('Authorization', `Bearer ${currentToken}`)
          return instance(originalRequest)
        }

        const shouldTryRefresh = !currentToken || tokenManager.IsTokenExpired()
        if (!shouldTryRefresh) {
          throw error
        }

        if (AxiosManager.isRefreshing) {
          return new Promise((resolve, reject) => {
            AxiosManager.refreshSubscribers.push({
              resolve: (token: string) => {
                originalRequest._retry = true
                originalRequest.headers.set('Authorization', `Bearer ${token}`)
                resolve(instance(originalRequest))
              },
              reject,
            })
          })
        }

        originalRequest._retry = true
        AxiosManager.isRefreshing = true

        try {
          const outcome = await tokenManager.RefreshAccessToken()

          if (outcome.status === 'ok') {
            AxiosManager.isRefreshing = false
            AxiosManager.onTokenRefreshed(outcome.token)
            originalRequest.headers.set('Authorization', `Bearer ${outcome.token}`)
            return instance(originalRequest)
          }

          if (outcome.status === 'transient') {
            const existing = tokenManager.GetToken()
            if (existing) {
              AxiosManager.onTokenRefreshed(existing)
            } else {
              AxiosManager.onTokenRefreshFailed(new Error('Refresh temporarily unavailable'))
            }
            return Promise.reject(error)
          }

          AxiosManager.onTokenRefreshFailed(new Error('Session expired'))
        } catch (e) {
          AxiosManager.onTokenRefreshFailed(e)
        } finally {
          AxiosManager.isRefreshing = false
        }

        redirectToLogin()
        throw error
      },
    )
  }

  private static onTokenRefreshed(token: string) {
    this.refreshSubscribers.forEach(cb => cb.resolve(token))
    this.refreshSubscribers = []
  }

  private static onTokenRefreshFailed(error: any) {
    this.refreshSubscribers.forEach(cb => cb.reject(error))
    this.refreshSubscribers = []
  }

  /**
   * Giden verilerde timezone içeren tarih değeri olup olmadığını recursive kontrol eder.
   * Date nesnesi veya timezone offset içeren string tespit edilirse console.error basar.
   * Sadece DEV ortamında çağrılır (geliştirici uyarısı amaçlı).
   */
  private static detectTimezone(
    data: unknown,
    source: string,
    url: string | undefined,
    path: string = '',
  ): void {
    if (data === null || data === undefined) return

    // Date nesnesi kontrolü
    if (data instanceof Date) {
      console.error(
        `🚨 TIMEZONE HATASI TESPİT EDİLDİ [${source}] — Date nesnesi gönderiliyor!`,
        `\n  URL: ${url}`,
        `\n  Alan: ${path || 'root'}`,
        `\n  Değer: ${data.toString()}`,
      )
      return
    }

    // String tarih kontrolü — timezone offset içeriyor mu? (+03:00, -05:30 ...)
    if (typeof data === 'string') {
      const timezoneOffsetRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([+-]\d{2}:\d{2})/
      if (timezoneOffsetRegex.test(data)) {
        console.error(
          `🚨 TIMEZONE HATASI TESPİT EDİLDİ [${source}] — Offset'li tarih string'i!`,
          `\n  URL: ${url}`,
          `\n  Alan: ${path || 'root'}`,
          `\n  Değer: ${data}`,
        )
      }
      return
    }

    // FormData kontrolü
    if (data instanceof FormData) {
      data.forEach((value, key) => {
        this.detectTimezone(value, source, url, `${path}.${key}`)
      })
      return
    }

    // Object/Array traversal
    if (typeof data === 'object') {
      for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        this.detectTimezone(value, source, url, path ? `${path}.${key}` : key)
      }
    }
  }
}
